import { useEffect, useState, useRef } from 'react'
import { getIgrejaByDomain } from '@/lib/igreja'
import { useTheme } from './useTheme'
import type { Database } from '@/types/database'

type Igreja = Database['public']['Tables']['igrejas']['Row']

// Cache global para evitar múltiplas requisições
let churchCache: Igreja | null = null
let cacheTimestamp: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

export function useChurch() {
  const [church, setChurch] = useState<Igreja | null>(churchCache)
  const [loading, setLoading] = useState(!churchCache)
  const [error, setError] = useState<string | null>(null)
  const { isDark } = useTheme()
  const fetchedRef = useRef(false)

  const fetchChurch = async (forceRefresh = false) => {
    try {
      // Verificar se temos cache válido e não é um refresh forçado
      const now = Date.now()
      if (!forceRefresh && churchCache && (now - cacheTimestamp) < CACHE_DURATION) {
        console.log('🏛️ useChurch - Usando cache válido')
        setChurch(churchCache)
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)
      
      console.log('🏛️ useChurch - Buscando dados da igreja...')
      const churchData = await getIgrejaByDomain()
      
      // Atualizar cache
      churchCache = churchData
      cacheTimestamp = now
      
      setChurch(churchData)
    } catch (err) {
      console.error('Erro ao buscar dados da igreja:', err)
      setError('Erro ao carregar dados da igreja')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Evitar múltiplas chamadas simultâneas
    if (!fetchedRef.current) {
      fetchedRef.current = true
      fetchChurch()
    }
  }, [])

  // Função para revalidar os dados da igreja
  const revalidate = async () => {
    await fetchChurch(true)
  }

  // Computed properties for theme-aware logo
  const currentLogo = church ? (isDark ? church.logo_dark : church.logo_light) : null
  const hasLogo = Boolean(currentLogo)

  // Computed properties for church colors with defaults
  const primaryColor = church?.cor_primaria || '#3b82f6' // Default blue
  const secondaryColor = church?.cor_secundaria || '#64748b' // Default slate

  // Theme-aware title and description colors
  const titleColor = isDark
    ? (church?.cor_titulo_dark || church?.cor_titulo || '#fb923c') // Default orange for dark
    : (church?.cor_titulo_light || church?.cor_titulo || '#f97316') // Default orange for light

  const descriptionColor = isDark
    ? (church?.cor_descricao_dark || church?.cor_descricao || '#94a3b8') // Default slate for dark
    : (church?.cor_descricao_light || church?.cor_descricao || '#64748b') // Default slate for light

  return {
    church,
    loading,
    error,
    currentLogo,
    hasLogo,
    isDark,
    primaryColor,
    secondaryColor,
    titleColor,
    descriptionColor,
    revalidate
  }
}